import { message } from 'antd'
import { useMemo, useRef, useState } from 'react'
import API from '@api/api'
import PromptIcon from '@assets/images/svgIcon/promptIcon.svg'
import Iconfont from '@utils/Iconfont'
import ProductInfoEditModal from './components/ProductInfoEditModal'
import styles from './index.module.scss'

const MAX_PRODUCT_INFO_LENGTH = 2000
const MAX_LENGTH_MESSAGE = '最多可输入2000字'
const MAX_LENGTH_MESSAGE_KEY = 'smart-detail-product-info-max-length'

const ProductInfoInput = ({
    value: controlledValue,
    onChange,
    detailImageList = [],
}) => {
    const [innerValue, setInnerValue] = useState('')
    const [editModalOpen, setEditModalOpen] = useState(false)
    const [analyzing, setAnalyzing] = useState(false)
    const analyzeRequestIdRef = useRef(0)

    const showMaxLengthMessage = () => {
        message.warning({
            content: MAX_LENGTH_MESSAGE,
            key: MAX_LENGTH_MESSAGE_KEY,
        })
    }

    const isControlled = useMemo(() => controlledValue !== undefined, [controlledValue])
    const value = isControlled ? controlledValue : innerValue

    const setValue = (nextValue) => {
        const safeValue = String(nextValue || '').slice(0, MAX_PRODUCT_INFO_LENGTH)
        if (!isControlled) {
            setInnerValue(safeValue)
        }
        onChange?.(safeValue)
    }

    const handleChange = (event) => {
        setValue(event.target.value)
    }

    const handleEdit = () => {
        setEditModalOpen(true)
    }

    const handleBeforeInput = (event) => {
        const currentValue = String(value || '')
        const insertedText = event?.data || ''
        const target = event?.target
        const selectionStart = target?.selectionStart ?? currentValue.length
        const selectionEnd = target?.selectionEnd ?? currentValue.length
        const nextLength = currentValue.length - (selectionEnd - selectionStart) + insertedText.length
        if (nextLength > MAX_PRODUCT_INFO_LENGTH) {
            showMaxLengthMessage()
        }
    }

    const handlePaste = (event) => {
        const currentValue = String(value || '')
        const pastedText = event?.clipboardData?.getData('text') || ''
        const target = event?.target
        const selectionStart = target?.selectionStart ?? currentValue.length
        const selectionEnd = target?.selectionEnd ?? currentValue.length
        const nextLength = currentValue.length - (selectionEnd - selectionStart) + pastedText.length
        if (nextLength > MAX_PRODUCT_INFO_LENGTH) {
            showMaxLengthMessage()
        }
    }

    const handleSaveEdit = (nextValue) => {
        setValue(nextValue)
        setEditModalOpen(false)
        message.success('保存成功')
    }

    const getPromptText = (data) => {
        if (typeof data === 'string') return data.trim()
        if (!Array.isArray(data)) return ''
        return data
            .map((item) => {
                if (item && typeof item === 'object' && typeof item.description === 'string') {
                    return item.description.trim()
                }
                if (typeof item === 'string') return item.trim()
                return ''
            })
            .filter(Boolean)
            .join('\n')
            .trim()
    }

    const getPromptImages = () => {
        return detailImageList
            .map((item, index) => {
                const url = item?.url || item?.imageUrl || ''
                if (!url) return null
                return {
                    url,
                    main: Boolean(item?.main ?? (index === 0)),
                }
            })
            .filter(Boolean)
    }

    const handleAnalyze = async () => {
        if (analyzing) return

        const images = getPromptImages()
        if (!images.length) {
            message.warning('请先上传详情页图片')
            return
        }

        const requestId = ++analyzeRequestIdRef.current
        setAnalyzing(true)
        try {
            const res = await API.doDetailPagePrompt(images)
            if (requestId !== analyzeRequestIdRef.current) return
            if (res?.code !== 0) {
                throw new Error(res?.message || 'AI分析失败，请重试')
            }

            const nextValue = getPromptText(res?.data)
            if (!nextValue) {
                message.warning('AI分析失败，请重试')
                return
            }
            setValue(nextValue)
        } catch (err) {
            if (requestId !== analyzeRequestIdRef.current) return
            message.error(err?.message || 'AI分析失败，请重试')
        } finally {
            if (requestId === analyzeRequestIdRef.current) {
                setAnalyzing(false)
            }
        }
    }

    return (
        <>
            <div className={styles.ProductInfoInput}>
                <div className={styles.InputArea}>
                    {!value && (
                        <div className={styles.Placeholder}>
                            {analyzing ? 'AI分析中，很快就好...' : '请填写商品描述，如款式品类、工艺细节、适用场景等'}
                        </div>
                    )}
                    <textarea
                        className={styles.Textarea}
                        value={value}
                        onChange={handleChange}
                        onBeforeInput={handleBeforeInput}
                        onPaste={handlePaste}
                        maxLength={MAX_PRODUCT_INFO_LENGTH}
                    />
                </div>
                <div className={styles.ActionRow}>
                    <button
                        type="button"
                        className={styles.EditButton}
                        onClick={handleEdit}
                        disabled={analyzing}
                    >
                        <Iconfont className={styles.EditIcon} type="icon-Material-EditOutlined" />
                        编辑
                    </button>
                    <button
                        type="button"
                        className={`${styles.AnalyzeButton} ${analyzing ? styles.AnalyzeButtonDisabled : ''}`}
                        onClick={handleAnalyze}
                        disabled={analyzing}
                    >
                        <img className={styles.AnalyzeIcon} src={PromptIcon} alt="" />
                        {analyzing ? '正在构思' : 'AI分析'}
                    </button>
                </div>
            </div>
            <ProductInfoEditModal
                open={editModalOpen}
                value={value}
                onCancel={() => setEditModalOpen(false)}
                onSave={handleSaveEdit}
                maxLength={MAX_PRODUCT_INFO_LENGTH}
                onMaxLengthReach={showMaxLengthMessage}
            />
        </>
    )
}

export default ProductInfoInput
